import { createSlice, current } from "@reduxjs/toolkit";
import { enrollmentApi } from "pages/slice";
import { formatDate } from "utils/dateUtils";
import { DateTime } from "luxon";
import { removePhoneMask } from "utils/phoneUtils";

const initialState = {
  enrollment: {
    id: "",
    email: "",
    phone: "",
    enrollmentType: "new",
    createdBy: "agent",
    serviceType: "",
    formData: {
      send_deposit_notification: false,
      send_enrollment_complete_notification: false,
    },
    deposit_amount: "",
  },
  tdspsWithOffers: [],
  selectedTdsp: {},
  selectedOffer: {},
  meters: [],
  bills: [],
  documents: [],
};

const clearFiles = (state, _, fileKey) => {
  state[fileKey] = [];
};

// updateFiles updates files for a given array in the store
const updateFiles = (state, action, fileKey) => {
  if (!Array.isArray(action.payload)) {
    const currentFiles = state[fileKey];
    state[fileKey] = currentFiles.map((curr) => {
      if (curr?.file?.file_name === action.payload.file_name) {
        return {
          ...curr,
          success: action.payload?.success,
          isUploading: action.payload?.isUploading,
          failed: action.payload?.failed,
        };
      }
      return curr;
    });
  } else if (
    JSON.stringify(action.payload) !== JSON.stringify(current(state[fileKey]))
  ) {
    state[fileKey] = action.payload;
  }
};

export const signupSlice = createSlice({
  name: "enrollment",
  initialState,
  reducers: {
    updateBillFiles: (state, action) => {
      updateFiles(state, action, "bills");
    },
    clearBillFiles: (state, action) => clearFiles(state, action, "bills"),
    updateDocumentFiles: (state, action) => {
      updateFiles(state, action, "documents");
    },
    clearDocumentFiles: (state, action) =>
      clearFiles(state, action, "documents"),
    updateForm: (state, action) => {
      const { formAction, formData, selectedTdsp, selectedOffer, isGeneralEnrollment } =
        action.payload;

      const {
        email,
        phone,
        meterIdentifier,
        meterIdentifier2,
        city,
        line1,
        line2,
        postal_code,
        state: a_state,
        nameKey,
        billingCycle,
        premise_type,
        isSameBillingAddress,
        company_name,
        first_name,
        last_name,
        birthday,
        promo_code,
        referral_name,
        referral_code,
        referral_type,
        ssn,
        preferredVerificationMethod,
        language,
        firstAvailableDate,
        requestedStartDate,
        enrollMode,
        meterUID, // esi id for TX
        isLmi,
        avgMonthlyBill,
        crm_lead_id,
      } = formData;

      const serviceAddress = {
        line1,
        line2,
        city,
        state: a_state,
        postal_code,
      };

      // all enrollments submitted from sales app will have these defaults
      const defaultValues = {
        memberAgreement: true,
        userAgreement: true,
        send_enrollment_complete_notification: true,
        send_deposit_notification: true,
      };

      const serviceType = selectedOffer.service_type;

      // battery products run credit in any state (e.g. IL), so their SSN /
      // credit-check fields are persisted just like TX enrollments
      let isBattery = false;
      try {
        isBattery =
          JSON.parse(selectedOffer.options)?.additional_service_type === "battery";
      } catch (e) {
        // do nothing
      }

      let formDataPayload = {
        ...state.enrollment.formData,
        ...defaultValues,
        firstName: first_name,
        lastName: last_name,
        birthdate:
          birthday === "" // 01/01/1900 used to indicate invalid date
            ? formatDate(DateTime.fromFormat("01/01/1900", "MM/dd/yyyy"))
            : formatDate(DateTime.fromFormat(birthday, "MM/dd/yyyy")),
        tdspName: selectedTdsp.name,
        tdsp: selectedTdsp,
        product: selectedOffer,
        address: serviceAddress,
        isSameBillingAddress: isSameBillingAddress,
        billingAddress: isSameBillingAddress
          ? serviceAddress
          : formData.billingAddress,
        meterUID: meterIdentifier,
        meterUID2: meterIdentifier2,
        nameKey: nameKey,
        billing_cycle: billingCycle,
        premise_type: premise_type,
        promoCode: promo_code,
        referralName: referral_name,
        referralCode: referral_code,
        referral_type: referral_type,
        sms_opted_in: preferredVerificationMethod === "sms",
        preferred_verification_method: preferredVerificationMethod,
        language: language || "en",
      };

      // persist SSN / credit-check fields for TX enrollments and battery products
      // (battery products run credit in any state, e.g. IL)
      if ((!isGeneralEnrollment || isBattery) && formAction !== "save") {
        // including ssn would trigger credit check
        let socialNumber = ssn;
        if (socialNumber && socialNumber !== "") {
          // strip any mask/formatting to raw digits
          const digits = removePhoneMask(socialNumber);
          // last-4 entry is left-padded with zeros to 9 (TransUnion guidance);
          // a full 9-digit entry is sent unchanged (no padding/truncation).
          socialNumber = digits.length === 4 ? `00000${digits}` : digits;

          formDataPayload.ssn = socialNumber;
          formDataPayload.agree_to_credit_check =
            socialNumber.length === 9 ? true : false;
        }
      }

      // add TX related fields
      if (!isGeneralEnrollment) {
        let startDate = firstAvailableDate
          ? ""
          : new Date(requestedStartDate).toISOString().slice(0, 10);

        formDataPayload.requestedStartDate = startDate;
        formDataPayload.firstAvailableDate = firstAvailableDate;
        formDataPayload.enrollMode = enrollMode;
        // tx form uses meterUID
        formDataPayload.meterUID = meterUID;
      } else { 
        // non texas form uses meterIdentifier
        formDataPayload.meterUID = meterIdentifier;

        // fields based on NTX service type
        if (serviceType === "community_solar") {
          formDataPayload.is_lmi = isLmi;
          formDataPayload.avgMonthlyBill = avgMonthlyBill;
        } else {
          formDataPayload.is_lmi = false;
          formDataPayload.avgMonthlyBill = "";
        }
      }

      if (premise_type === "commercial") {
        formDataPayload.company_name = company_name;
      }

      state.enrollment = {
        ...state.enrollment,
        email: email,
        phone: phone !== "" ? `+1${removePhoneMask(phone)}` : "", // assuming US number
        serviceType: serviceType,
        formData: formDataPayload,
        ...(crm_lead_id ? { crm_lead_id } : {}),
      };
    },
    saveSalesChannel: (state, action) => {
      const { salesChannelName, salesChannelCode } = action.payload;

      // stamp the sales channel onto form_data so it is included on submit.
      // updateForm spreads existing formData, so these values persist.
      state.enrollment = {
        ...state.enrollment,
        formData: {
          ...state.enrollment.formData,
          salesChannelName,
          salesChannelCode,
        },
      };
    },
    updateReqStartDate: (state, action) => {
      const { requestedStartDate } = action.payload;

      // non TX enrollment start date determined by tdsp & billing cycle and set here
      state.enrollment = {
        ...state.enrollment,
        formData: {
          ...state.enrollment.formData,
          requestedStartDate,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      enrollmentApi.endpoints.getServiceAddresses.matchFulfilled,
      (state, action) => {
        state.meters = action.payload.meters;
      }
    );
    builder.addMatcher(
      enrollmentApi.endpoints.getTdspOffers.matchFulfilled,
      (state, action) => {
        state.tdspsWithOffers = action.payload.tdsps;
      }
    );
    builder.addMatcher(
      enrollmentApi.endpoints.getEnrollment.matchFulfilled,
      (state, action) => {
        state.enrollment = {
          ...state.enrollment,
          ...action.payload,
        };
        // an unassessed deposit comes back as 0; reset it to empty like a new
        // form so a reopened enrollment still runs credit (GX-7563)
        if (!action.payload?.deposit_amount_assessed) {
          state.enrollment.deposit_amount = "";
        }
      }
    );
    builder.addMatcher(
      enrollmentApi.endpoints.getDeposit.matchFulfilled,
      (state, action) => {
        state.enrollment.deposit_amount = action.payload.depositAmount;
      }
    );
    builder.addMatcher(
      enrollmentApi.endpoints.updateEnrollment.matchFulfilled,
      (state, action) => {
        // persist id & status to enrollment data after save/submit
        state.enrollment.id = action.payload.enrollmentID;
        state.enrollment.enrollmentStatus = action.payload.status;
      }
    );
  },
});


export const {
  updateForm,
  updateDocumentFiles,
  updateBillFiles,
  clearBillFiles,
  clearDocumentFiles,
  updateReqStartDate,
  saveSalesChannel,
} = signupSlice.actions;
export default signupSlice.reducer;
